import React, { useState, useEffect } from 'react';
import { Database, Link2, Plus, Trash2, Edit2, Play, Pause, Save, X, ToggleLeft, ToggleRight, CheckCircle2, AlertCircle, FileSpreadsheet, Send, MessageSquareCode } from 'lucide-react';
import { getAppConnections, createAppConnection, updateAppConnection, deleteAppConnection } from '../services/api';

interface AppConnection {
    id: number;
    name: string;
    platform: string;
    api_url: string;
    api_token?: string;
    config: any;
    is_enabled: boolean;
}

interface IntegrationsManagerProps {
    toast?: {
        success: (message: string) => void;
        error: (message: string) => void;
        warning: (message: string) => void;
        info: (message: string) => void;
    };
}

export const IntegrationsManager: React.FC<IntegrationsManagerProps> = ({ toast }) => {
    const [connections, setConnections] = useState<AppConnection[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingConn, setEditingConn] = useState<AppConnection | null>(null);

    // Form states
    const [name, setName] = useState('');
    const [platform, setPlatform] = useState('botadesk');
    const [apiUrl, setApiUrl] = useState('');
    const [apiToken, setApiToken] = useState('');
    const [isEnabled, setIsEnabled] = useState(true);
    // Platform-specific configs
    const [defaultSku, setDefaultSku] = useState('');
    const [sheetId, setSheetId] = useState('');
    const [chatId, setChatId] = useState('');

    useEffect(() => {
        loadConnections();
    }, []);

    const loadConnections = async () => {
        try {
            setIsLoading(true);
            const res = await getAppConnections();
            if (res.success) {
                setConnections(res.connections || []);
            }
        } catch (err: any) {
            console.error('Error loading connections:', err);
            toast?.error('Error al cargar conexiones: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenCreateModal = (plat: string) => {
        setEditingConn(null);
        setPlatform(plat);
        setIsEnabled(true);
        setApiToken('');
        
        // Defaults
        if (plat === 'botadesk') {
            setName('BotAdesk Integración');
            setApiUrl('https://botadesk.dxsystem.store');
            setDefaultSku('');
        } else if (plat === 'google_sheets') {
            setName('Google Sheets Webhook');
            setApiUrl('');
            setSheetId('');
        } else if (plat === 'telegram') {
            setName('Telegram Bot Alert');
            setApiUrl('https://api.telegram.org/bot/sendMessage'); // Telegram base endpoint placeholder
            setChatId('');
        } else {
            setName('Mi Webhook Genérico');
            setApiUrl('');
        }
        
        setShowModal(true);
    };

    const handleOpenEditModal = (conn: AppConnection) => {
        setEditingConn(conn);
        setName(conn.name);
        setPlatform(conn.platform);
        setApiUrl(conn.api_url);
        setApiToken(conn.api_token || '');
        setIsEnabled(conn.is_enabled);

        // Parse config
        let cfg: any = {};
        if (conn.config) {
            try {
                cfg = typeof conn.config === 'string' ? JSON.parse(conn.config) : conn.config;
            } catch (e) {
                cfg = conn.config;
            }
        }

        setDefaultSku(cfg.default_sku || '');
        setSheetId(cfg.sheet_id || '');
        setChatId(cfg.chat_id || '');

        setShowModal(true);
    };

    const handleSaveConnection = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name || !apiUrl) {
            toast?.error('El nombre y el URL del API son requeridos');
            return;
        }

        // Build config
        const configData: any = {};
        if (platform === 'botadesk') {
            configData.default_sku = defaultSku;
        } else if (platform === 'google_sheets') {
            configData.sheet_id = sheetId;
        } else if (platform === 'telegram') {
            configData.chat_id = chatId;
        }

        const payload = {
            name,
            platform,
            apiUrl,
            apiToken,
            isEnabled,
            config: configData
        };

        try {
            setIsLoading(true);
            let res;
            if (editingConn) {
                res = await updateAppConnection(editingConn.id, payload);
                if (res.success) {
                    toast?.success('Conexión actualizada exitosamente');
                }
            } else {
                res = await createAppConnection(payload);
                if (res.success) {
                    toast?.success('Nueva conexión añadida exitosamente');
                }
            }
            setShowModal(false);
            loadConnections();
        } catch (err: any) {
            console.error('Error saving connection:', err);
            toast?.error('Error al guardar: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteConnection = async (id: number) => {
        if (!confirm('¿Estás seguro de que deseas eliminar esta conexión?')) return;
        try {
            setIsLoading(true);
            const res = await deleteAppConnection(id);
            if (res.success) {
                toast?.success('Conexión eliminada');
                loadConnections();
            }
        } catch (err: any) {
            console.error('Error deleting connection:', err);
            toast?.error('Error al eliminar: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleEnabled = async (conn: AppConnection) => {
        try {
            const res = await updateAppConnection(conn.id, { isEnabled: !conn.is_enabled });
            if (res.success) {
                toast?.success(conn.is_enabled ? 'Conexión desactivada' : 'Conexión activada');
                loadConnections();
            }
        } catch (err: any) {
            console.error('Error toggling connection state:', err);
            toast?.error('Error al cambiar estado: ' + err.message);
        }
    };

    const getPlatformIcon = (plat: string, size = 20) => {
        switch (plat) {
            case 'botadesk':
                return <MessageSquareCode size={size} className="text-blue-500" />;
            case 'google_sheets':
                return <FileSpreadsheet size={size} className="text-green-600" />;
            case 'telegram':
                return <Send size={size} className="text-sky-500" />;
            default:
                return <Link2 size={size} className="text-indigo-500" />;
        }
    };

    const getPlatformName = (plat: string) => {
        switch (plat) {
            case 'botadesk': return 'BotAdesk';
            case 'google_sheets': return 'Google Sheets';
            case 'telegram': return 'Telegram Alert';
            default: return 'Custom HTTP Webhook';
        }
    };

    return (
        <div className="flex-1 flex flex-col h-full overflow-hidden p-6 bg-theme-base">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-theme-main flex items-center gap-2">
                        <Database className="text-primary-600" /> Directorio de Integraciones Outbound
                    </h2>
                    <p className="text-sm text-theme-muted mt-1">
                        Conecta y alimenta al chatbot con APIs y aplicaciones externas para delegar flujos automáticos.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 overflow-hidden">
                {/* Left side: Directory templates */}
                <div className="lg:col-span-1 bg-theme-card p-6 rounded-xl border border-theme flex flex-col overflow-hidden">
                    <h3 className="text-sm font-semibold text-theme-main mb-4 uppercase tracking-wider">Aplicaciones Disponibles</h3>
                    <div className="space-y-4 overflow-y-auto custom-scrollbar flex-1 pr-1">
                        {/* BotAdesk template card */}
                        <div className="p-4 rounded-xl border border-theme hover:border-blue-300 bg-theme-base/50 transition-all flex flex-col justify-between h-44">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    {getPlatformIcon('botadesk', 24)}
                                    <h4 className="font-bold text-theme-main text-sm">BotAdesk M365/Autodesk</h4>
                                </div>
                                <p className="text-xs text-theme-muted line-clamp-3">
                                    Crea usuarios de Microsoft 365, asigna licencias y despacha invitaciones de Autodesk directamente desde conversaciones de WhatsApp.
                                </p>
                            </div>
                            <button
                                onClick={() => handleOpenCreateModal('botadesk')}
                                className="w-full mt-2 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                            >
                                <Plus size={14} /> Conectar BotAdesk
                            </button>
                        </div>

                        {/* Google Sheets template card */}
                        <div className="p-4 rounded-xl border border-theme hover:border-green-300 bg-theme-base/50 transition-all flex flex-col justify-between h-44">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    {getPlatformIcon('google_sheets', 24)}
                                    <h4 className="font-bold text-theme-main text-sm">Google Sheets Webhook</h4>
                                </div>
                                <p className="text-xs text-theme-muted line-clamp-3">
                                    Registra información del cliente, registros de compras, leads de venta u hojas de contacto directamente en filas de Google Sheets.
                                </p>
                            </div>
                            <button
                                onClick={() => handleOpenCreateModal('google_sheets')}
                                className="w-full mt-2 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                            >
                                <Plus size={14} /> Conectar Sheets
                            </button>
                        </div>

                        {/* Telegram template card */}
                        <div className="p-4 rounded-xl border border-theme hover:border-sky-300 bg-theme-base/50 transition-all flex flex-col justify-between h-44">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    {getPlatformIcon('telegram', 24)}
                                    <h4 className="font-bold text-theme-main text-sm">Telegram Bot alerts</h4>
                                </div>
                                <p className="text-xs text-theme-muted line-clamp-3">
                                    Envía notificaciones de alerta inmediata y reportes a canales, grupos o chats privados de Telegram gestionados por tu equipo de soporte.
                                </p>
                            </div>
                            <button
                                onClick={() => handleOpenCreateModal('telegram')}
                                className="w-full mt-2 py-1.5 bg-sky-500 hover:bg-sky-600 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                            >
                                <Plus size={14} /> Conectar Telegram
                            </button>
                        </div>

                        {/* Custom webhook card */}
                        <div className="p-4 rounded-xl border border-theme hover:border-indigo-300 bg-theme-base/50 transition-all flex flex-col justify-between h-44">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    {getPlatformIcon('custom_webhook', 24)}
                                    <h4 className="font-bold text-theme-main text-sm">Webhook Custom HTTP</h4>
                                </div>
                                <p className="text-xs text-theme-muted line-clamp-3">
                                    Conecta con cualquier API externa REST. Envía cargas de trabajo customizadas y recibe de vuelta el texto de confirmación formateado.
                                </p>
                            </div>
                            <button
                                onClick={() => handleOpenCreateModal('custom_webhook')}
                                className="w-full mt-2 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                            >
                                <Plus size={14} /> Conectar Webhook
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right side: Configured integrations list */}
                <div className="lg:col-span-2 bg-theme-card p-6 rounded-xl border border-theme flex flex-col overflow-hidden">
                    <h3 className="text-sm font-semibold text-theme-main mb-4 uppercase tracking-wider flex items-center justify-between">
                        Conexiones Activas
                        <span className="bg-slate-200 text-theme-muted px-2 py-0.5 rounded text-xs">{connections.length}</span>
                    </h3>

                    <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-1">
                        {connections.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center p-8 border-2 border-dashed border-theme rounded-xl">
                                <Link2 size={32} className="text-slate-300 mb-2" />
                                <h4 className="font-semibold text-theme-main text-sm">No hay conexiones activas</h4>
                                <p className="text-xs text-theme-muted max-w-sm mt-1">
                                    Selecciona una aplicación del panel izquierdo para empezar a delegar tareas desde tu chatbot de IA.
                                </p>
                            </div>
                        ) : (
                            connections.map(conn => (
                                <div key={conn.id} className={`p-4 rounded-xl border transition-all bg-theme-base flex items-start justify-between gap-4 ${conn.is_enabled ? 'border-theme hover:border-slate-300' : 'border-theme opacity-60'}`}>
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className="p-2.5 bg-theme-card rounded-lg border border-theme shadow-sm mt-0.5">
                                            {getPlatformIcon(conn.platform, 20)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <h4 className="font-bold text-theme-main text-sm truncate">{conn.name}</h4>
                                                <span className="text-[10px] bg-slate-200 text-theme-muted px-2 py-0.5 rounded-full font-medium">
                                                    {getPlatformName(conn.platform)}
                                                </span>
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${conn.is_enabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                                                    {conn.is_enabled ? 'Activo' : 'Desactivado'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-theme-muted font-mono truncate mb-1">
                                                <span className="font-medium text-slate-400">Endpoint:</span> {conn.api_url}
                                            </p>
                                            <p className="text-[11px] text-theme-muted">
                                                <span className="font-medium">ID de App:</span> <code className="bg-theme-card px-1 py-0.5 rounded border border-theme text-primary-600 font-bold">{conn.id}</code>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => handleToggleEnabled(conn)}
                                            className={`p-1.5 rounded transition-colors ${conn.is_enabled ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-100'}`}
                                            title={conn.is_enabled ? 'Desactivar conexión' : 'Activar conexión'}
                                        >
                                            {conn.is_enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                        </button>
                                        <button
                                            onClick={() => handleOpenEditModal(conn)}
                                            className="p-1.5 rounded text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                                            title="Editar conexión"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteConnection(conn.id)}
                                            className="p-1.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                            title="Eliminar conexión"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Config modal */}
            {showModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
                    <div className="bg-theme-card rounded-xl shadow-xl border border-theme w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="flex justify-between items-center p-5 border-b border-theme">
                            <h3 className="font-bold text-theme-main text-lg flex items-center gap-2">
                                {getPlatformIcon(platform, 20)}
                                {editingConn ? 'Editar Conexión' : `Conectar a ${getPlatformName(platform)}`}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSaveConnection} className="flex-1 overflow-y-auto p-5 space-y-4">
                            {/* App Name */}
                            <div>
                                <label className="block text-xs font-semibold text-theme-main mb-1.5 uppercase tracking-wider">Nombre Identificador</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full border border-theme rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 bg-theme-base text-theme-main"
                                    placeholder="Ej: Licenciador Autodesk Oficina"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                />
                            </div>

                            {/* Endpoint URL */}
                            <div>
                                <label className="block text-xs font-semibold text-theme-main mb-1.5 uppercase tracking-wider">URL del Endpoint / API</label>
                                <input
                                    type="url"
                                    required
                                    className="w-full border border-theme rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 bg-theme-base text-theme-main"
                                    placeholder="https://api.ejemplo.com/endpoint"
                                    value={apiUrl}
                                    onChange={e => setApiUrl(e.target.value)}
                                />
                            </div>

                            {/* API Token / Key */}
                            <div>
                                <label className="block text-xs font-semibold text-theme-main mb-1.5 uppercase tracking-wider">Token de Autorización / API Key</label>
                                <input
                                    type="text"
                                    className="w-full border border-theme rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 bg-theme-base text-theme-main"
                                    placeholder="Bearer u otro token de acceso (opcional)"
                                    value={apiToken}
                                    onChange={e => setApiToken(e.target.value)}
                                />
                            </div>

                            {/* Platform specific configurations */}
                            {platform === 'botadesk' && (
                                <div>
                                    <label className="block text-xs font-semibold text-theme-main mb-1.5 uppercase tracking-wider">Licencia SKU Predeterminada (Opcional)</label>
                                    <input
                                        type="text"
                                        className="w-full border border-theme rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 bg-theme-base text-theme-main"
                                        placeholder="Ej: cbdc14ab-d96c-4c30-b9f4-6e85da156a7d"
                                        value={defaultSku}
                                        onChange={e => setDefaultSku(e.target.value)}
                                    />
                                </div>
                            )}

                            {platform === 'google_sheets' && (
                                <div>
                                    <label className="block text-xs font-semibold text-theme-main mb-1.5 uppercase tracking-wider">Spreadsheet ID / Id de la Hoja (Opcional)</label>
                                    <input
                                        type="text"
                                        className="w-full border border-theme rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 bg-theme-base text-theme-main"
                                        placeholder="ID de la hoja de cálculo de Google"
                                        value={sheetId}
                                        onChange={e => setSheetId(e.target.value)}
                                    />
                                </div>
                            )}

                            {platform === 'telegram' && (
                                <div>
                                    <label className="block text-xs font-semibold text-theme-main mb-1.5 uppercase tracking-wider">Chat ID de Destino</label>
                                    <input
                                        type="text"
                                        className="w-full border border-theme rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 bg-theme-base text-theme-main"
                                        placeholder="Ej: -100123456789 (ID de canal o grupo)"
                                        value={chatId}
                                        onChange={e => setChatId(e.target.value)}
                                    />
                                </div>
                            )}

                            {/* Connection status */}
                            <div className="flex items-center gap-2 pt-2">
                                <input
                                    type="checkbox"
                                    id="isEnabled"
                                    className="rounded border-theme text-blue-600 focus:ring-blue-500 bg-theme-base"
                                    checked={isEnabled}
                                    onChange={e => setIsEnabled(e.target.checked)}
                                />
                                <label htmlFor="isEnabled" className="text-sm font-semibold text-theme-main">
                                    Habilitar conexión inmediatamente
                                </label>
                            </div>

                            {/* Footer Buttons */}
                            <div className="flex justify-end gap-2 pt-4 border-t border-theme">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 bg-theme-base hover:bg-slate-100 rounded-lg text-sm text-theme-muted border border-theme font-medium transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-bold flex items-center gap-1 transition-colors"
                                >
                                    <Save size={16} /> Guardar Conexión
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
